import { computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useConfirm } from 'primevue/useconfirm';
import { useAuth } from '@/composables/useAuth';
import { useInboxSummary } from '@/composables/useInboxSummary';
import { useComplaintsSummary } from '@/composables/useComplaintsSummary';
import { countryScope } from '@/composables/useCountryScopeFilter';
import { useAttendanceSync } from '@/composables/useAttendanceSync';
import { useSettingsPermissions } from '@/features/settings/composables/useSettingsPermissions';
import { useGlobalSettings } from '@/features/settings/composables/useGlobalSettings';
import {
  shellNavSections,
  getShellTabByKey,
  getShellAdminTabByKey
} from '../data/shellConfig';
import {
  buildShellAuthContext,
  canSeeShellTab,
  canSeeShellAdminTab
} from '../utils/shellTabVisibility';
import {
  formatUserDisplayName,
  getInitials,
  resolvePrimaryRoleLabel
} from '../utils/sidebarUserUtils';
import { resolveAvatarUrl } from '@/utils/resolveAvatarUrl';

/** shellConfig icons: `pi-chart-bar` or short `building` → PrimeIcons suffix. */
const toPrimeIconClass = (icon) => {
  if (!icon) return '';
  return icon.startsWith('pi-') ? icon : `pi-${icon}`;
};

function formatInboxAriaLabel(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return 'Inbox';
  if (n === 1) return 'Inbox, 1 item needs action';
  const capped = n > 99 ? 99 : n;
  if (n > 99) return `Inbox, ${n} items need action`;
  return `Inbox, ${capped} items need action`;
}

function formatInboxRailTooltip(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return 'Inbox';
  return `Inbox · ${n} action required`;
}

/** Overseers count open items in their scope; the dot means own action. */
function inboxOpenLabel(inbox) {
  const open = `${inbox.open} open item${inbox.open === 1 ? '' : 's'}`;
  const own = inbox.pending > 0 ? `, ${inbox.pending} need${inbox.pending === 1 ? 's' : ''} your action` : '';
  return `${open}${own}`;
}

function buildNavItem(
  tab,
  inbox = { pending: 0, open: 0, openScope: null },
  complaints = { open: 0 }
) {
  const inboxPendingCount = inbox.pending;
  const showOpen = tab.key === 'inbox' && Boolean(inbox.openScope);
  const inboxBadge = showOpen
    ? (inbox.open > 0 ? inbox.open : null)
    : tab.key === 'inbox' && inboxPendingCount > 0 ? inboxPendingCount : null;
  const complaintOpenCount = Number(complaints.open) || 0;
  const badge = tab.key === 'complaints' && complaintOpenCount > 0
    ? complaintOpenCount
    : inboxBadge;
  const isComplaintQueue = tab.key === 'complaints' && complaintOpenCount > 0;
  return {
    key: tab.key,
    label: tab.label,
    icon: toPrimeIconClass(tab.icon),
    path: tab.path,
    routeName: tab.routeName,
    relatedRouteNames: tab.relatedRouteNames,
    isDisabled: !!tab.isPlaceholder,
    statusBadge: tab.isPlaceholder ? 'Soon' : null,
    badgeKind: tab.isPlaceholder ? 'status' : ['inbox', 'complaints'].includes(tab.key) ? 'count' : null,
    badge,
    showAlertDot: tab.key === 'inbox' && inboxPendingCount > 0,
    ariaLabel: isComplaintQueue
      ? `${tab.label}, ${complaintOpenCount} open message${complaintOpenCount === 1 ? '' : 's'}`
      : tab.key !== 'inbox'
      ? tab.label
      : showOpen ? `Inbox, ${inboxOpenLabel(inbox)}` : formatInboxAriaLabel(inboxPendingCount),
    railTooltip: isComplaintQueue
      ? `${tab.label} · ${complaintOpenCount} open`
      : tab.key !== 'inbox'
      ? tab.label
      : showOpen ? `Inbox · ${inboxOpenLabel(inbox)}` : formatInboxRailTooltip(inboxPendingCount)
  };
}

function buildAdminNavItem(tab) {
  return {
    key: tab.key,
    label: tab.label,
    icon: toPrimeIconClass(tab.icon),
    path: tab.path,
    routeName: null,
    relatedRouteNames: null,
    isDisabled: false,
    statusBadge: null,
    badgeKind: null,
    badge: null,
    showAlertDot: false,
    ariaLabel: tab.label,
    railTooltip: tab.label
  };
}

function resolveSectionItem(item, ctx, inbox, complaints) {
  if (item.type === 'tab') {
    const tab = getShellTabByKey(item.tabKey);
    if (!tab || tab.hidden || !canSeeShellTab(tab, ctx)) return null;
    const navItem = buildNavItem(tab, inbox, complaints);
    if (tab.key === 'job-descriptions' && ctx.isRegularUser) {
      navItem.label = 'My job description';
      navItem.ariaLabel = 'My job description';
      navItem.railTooltip = 'My job description';
      navItem.to = { name: 'my-profile', query: { tab: 'job-description' } };
    }
    return navItem;
  }

  if (item.type === 'adminTab') {
    const tab = getShellAdminTabByKey(item.tabKey);
    if (!tab || tab.hidden || !canSeeShellAdminTab(tab, ctx)) return null;
    return buildAdminNavItem(tab);
  }

  return null;
}

/**
 * Resolves shellNavSections + RBAC into sidebar nav model.
 * Sidebar.vue consumes navSectionGroups only — no permission logic in the view.
 */
export function useSidebarNav() {
  const router = useRouter();
  const route = useRoute();
  const store = useStore();
  const confirm = useConfirm();
  const auth = useAuth();
  const { hasAnySection } = useSettingsPermissions();
  const { open: openSettingsModal } = useGlobalSettings();
  const {
    pendingCount: inboxPendingCount,
    openCount: inboxOpenCount,
    openScope: inboxOpenScope,
    refresh: refreshInboxSummary,
    invalidate: invalidateInboxSummary,
    reset: resetInboxSummary
  } = useInboxSummary();
  const {
    openCount: complaintsOpenCount,
    refresh: refreshComplaintsSummary,
    reset: resetComplaintsSummary
  } = useComplaintsSummary();

  let inboxLoadTimer = null;
  let complaintsLoadTimer = null;

  const user = computed(() => store.state.users.user);
  const isLogged = computed(() => !!store.state.users?.user?.isLogged);
  const isMobileSidebar = computed(() => store.state.modals.sidebar.isMobile);

  const authContext = computed(() => buildShellAuthContext(auth));

  useAttendanceSync(({ reason } = {}) => {
    if (route.name === 'inbox' && ['poll', 'focus', 'resume'].includes(reason)) return;
    return refreshInboxSummary();
  }, {
    enabled: () => isLogged.value && canSeeShellTab(getShellTabByKey('inbox'), authContext.value)
  });

  const canViewNotifications = computed(
    () =>
      isLogged.value &&
      (auth.hasRole('super_admin') || auth.hasRole('dept_admin'))
  );

  // RBAC + inbox badge only — route active state is resolved in SidebarNavSection.
  const navSectionGroups = computed(() => {
    const ctx = authContext.value;
    const inbox = {
      pending: Number(inboxPendingCount.value) || 0,
      open: Number(inboxOpenCount.value) || 0,
      openScope: inboxOpenScope.value
    };
    const complaints = {
      open: Number(complaintsOpenCount.value) || 0
    };
    const groups = [];

    for (const section of shellNavSections) {
      const items = [];

      for (const itemRef of section.items || []) {
        const navItem = resolveSectionItem(itemRef, ctx, inbox, complaints);
        if (navItem) items.push(navItem);
      }

      if (items.length === 0) continue;

      groups.push({
        id: section.id,
        label: section.label,
        items
      });
    }

    return groups;
  });

  const userCard = computed(() => {
    const displayName = formatUserDisplayName(user.value?.name, user.value?.email);
    const subtitle =
      resolvePrimaryRoleLabel(user.value?.roles) || user.value?.email || 'Signed in';

    return {
      name: displayName,
      subtitle,
      initials: getInitials(displayName),
      avatarUrl: resolveAvatarUrl(user.value?.avatarUrl)
    };
  });

  const goToMyProfile = () => {
    if (!isLogged.value) return;
    if (user.value?.id) {
      router.push({ name: 'my-profile' }).catch(() => {});
      return;
    }
    openSettingsModal('account');
  };

  const accountMenuActions = computed(() => {
    if (!isLogged.value) return [];

    const items = [
      {
        label: 'My Profile',
        icon: 'pi pi-user',
        command: () => {
          goToMyProfile();
        }
      }
    ];

    if (canViewNotifications.value) {
      items.push({
        label: 'Notifications',
        icon: 'pi pi-megaphone',
        command: () => {
          router.push('/notifications').catch(() => {});
        }
      });
    }

    if (hasAnySection.value) {
      items.push({
        label: 'Settings',
        icon: 'pi pi-cog',
        command: () => {
          openSettingsModal();
        }
      });
    }

    return items;
  });

  const showSidebarBehaviorControls = computed(() => !isMobileSidebar.value);

  const logout = () => {
    confirm.require({
      message: 'Are you sure you want to log out?',
      header: 'Log out',
      icon: 'pi pi-sign-out',
      acceptLabel: 'Log out',
      rejectLabel: 'Cancel',
      accept: () => {
        store.commit('users/logoutUser');
        router.push('/');
      }
    });
  };

  const scheduleInboxSummaryLoad = () => {
    if (inboxLoadTimer) clearTimeout(inboxLoadTimer);
    inboxLoadTimer = setTimeout(() => {
      if (!isLogged.value) {
        inboxPendingCount.value = 0;
        return;
      }
      refreshInboxSummary();
      inboxLoadTimer = null;
    }, 200);
  };

  const scheduleComplaintsSummaryLoad = () => {
    if (complaintsLoadTimer) clearTimeout(complaintsLoadTimer);
    complaintsLoadTimer = setTimeout(() => {
      if (!isLogged.value || !auth.hasRole('super_admin')) {
        resetComplaintsSummary();
        complaintsLoadTimer = null;
        return;
      }
      refreshComplaintsSummary();
      complaintsLoadTimer = null;
    }, 200);
  };

  onMounted(() => {
    scheduleInboxSummaryLoad();
    scheduleComplaintsSummaryLoad();
  });

  onBeforeUnmount(() => {
    if (inboxLoadTimer) {
      clearTimeout(inboxLoadTimer);
      inboxLoadTimer = null;
    }
    if (complaintsLoadTimer) {
      clearTimeout(complaintsLoadTimer);
      complaintsLoadTimer = null;
    }
  });

  watch(
    () => [isLogged.value, user.value?.id],
    () => {
      resetInboxSummary();
      resetComplaintsSummary();
      scheduleInboxSummaryLoad();
      scheduleComplaintsSummaryLoad();
    }
  );

  watch(countryScope, () => {
    resetComplaintsSummary();
    scheduleComplaintsSummaryLoad();
  });

  // Refresh badge after leaving Inbox (actions invalidate inline; this catches navigation-only exits).
  watch(
    () => route.name,
    (name, prevName) => {
      if (prevName === 'inbox' && name !== 'inbox') {
        scheduleInboxSummaryLoad();
      }
      if (prevName === 'complaints' && name !== 'complaints') {
        scheduleComplaintsSummaryLoad();
      }
    }
  );

  return {
    navSectionGroups,
    userCard,
    accountMenuActions,
    showSidebarBehaviorControls,
    inboxNeedsCount: inboxPendingCount,
    invalidateInboxSummary,
    goToMyProfile,
    logout
  };
}
